import { useMutation, useQuery } from "@tanstack/react-query";
import { createRootRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMachine } from "@xstate/react";
import {
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRight,
  RefreshCw,
  Rows3,
  Sun,
} from "lucide-react";
import { type JSX, useEffect, useMemo, useRef, useState } from "react";
import tarskiLockupLight from "../../design/tarski-kit/assets/tarski-logo-blue.svg";
import tarskiLockupDark from "../../design/tarski-kit/assets/tarski-logo-dark-lockup.svg";
import { readWebStudioFixtureSessionLabel } from "../api/auth";
import { classifyBootSnapshot, type WebStudioBootSnapshot } from "../api/boot";
import { commandEntriesFromCatalog, type WebStudioCommandEntry } from "../api/commandCatalog";
import { WebStudioCloudApiClient, type WebStudioDiagnostic } from "../api/generatedClient";
import { buildMutationEnvelope } from "../api/mutationEnvelope";
import { createWebStudioFetchTransport, subscribeWebStudioAuthSessionLoss } from "../api/transport";
import {
  catalogOptionsFromRows,
  environmentSelectionOutcome,
  selectWorkspace,
  selectWorkspaceEnvironment,
  selectWorldview,
  type WebStudioSelectionOutcome,
  workspaceSelectionOutcome,
  worldviewSelectionOutcome,
} from "../api/workspaces";
import {
  captureIntendedStudioLocation,
  consumeDeepLinkRepair,
  DEFAULT_STUDIO_DESTINATION,
  resetIntendedStudioLocation,
  takeIntendedStudioLocation,
} from "../app/deepLinkMemory";
import { destinationIcon } from "../app/destinationIcons";
import { allDestinations, STUDIO_NAV_GROUPS } from "../app/navigation";
import { commandCatalogQueryOptions } from "../app/queries/commandCatalog";
import { workspaceCatalogQueryOptions } from "../app/queries/workspaces";
import { useBootRefresh } from "../app/refresh";
import { scopeFromSearch, validateScopeSearch } from "../app/scope";
import {
  ScopeSwitchContext,
  type ScopeSwitchField,
  type ScopeSwitchValue,
  SEARCH_KEY_BY_SWITCH_FIELD,
} from "../app/scopeSwitch";
import { type SessionMode, webStudioSessionMachine } from "../app/sessionMachine";
import {
  ShellInspectorDock,
  ShellInspectorProvider,
  useShellInspector,
} from "../app/shellInspector";
import { useBootRead } from "../app/useBootRead";
import {
  AppShell,
  Button,
  CommandPalette,
  IconButton,
  StatusPill,
  Toast,
  useAppearance,
  useCommandK,
} from "../design";
import { SignInGate } from "../features/auth-gate";
import { AppScopeControls } from "../shared/components/AppScopeControls";
import { SessionModeBanner } from "../shared/components/SessionModeBanner";
import { clearProtectedWebStudioBrowserState } from "../state/browserState";

export const Route = createRootRoute({
  validateSearch: validateScopeSearch,
  component: RootShell,
});

function RootShell(): JSX.Element {
  return (
    <ShellInspectorProvider>
      <RootLayout />
    </ShellInspectorProvider>
  );
}

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 900,
  );
  useEffect(() => {
    const query = window.matchMedia("(max-width: 899px)");
    const onChange = (): void => setNarrow(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

/** Non-protected UI preference key: sidebar/rail choice only. */
const NAV_STORAGE_KEY = "jacqos.web_studio.ui.nav";

function readStoredNavCollapsed(): boolean {
  try {
    const raw = window.localStorage.getItem(NAV_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>).collapsed === true
      : false;
  } catch {
    return false;
  }
}

interface ScopeSwitchRequest {
  field: ScopeSwitchField;
  requested: string;
}

type ScopeSwitchState =
  | { kind: "idle" }
  | ({ kind: "pending" } & ScopeSwitchRequest)
  | ({ kind: "failed"; code: string; message: string } & ScopeSwitchRequest);

const BROWSER_ACTOR = { actorKind: "browser_client", actorId: "web-studio-browser" } as const;

const PALETTE_COMMAND_PREFIX = "backend-command:";

function RootLayout(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const appearance = useAppearance();
  const { token, requestRefresh } = useBootRefresh();
  const scope = useMemo(() => scopeFromSearch(search), [search]);
  const read = useBootRead(scope);
  const client = useMemo(() => new WebStudioCloudApiClient(createWebStudioFetchTransport()), []);
  const [session, sendSession] = useMachine(webStudioSessionMachine);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [commandNotice, setCommandNotice] = useState<string | null>(null);
  const [scopeSwitch, setScopeSwitch] = useState<ScopeSwitchState>({ kind: "idle" });
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => readStoredNavCollapsed());
  const narrow = useIsNarrow();
  const inspector = useShellInspector();

  useCommandK(() => setPaletteOpen((open) => !open));

  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify({ collapsed: navCollapsed }));
    } catch {
      // Preference persistence is best-effort; the session state still applies.
    }
  }, [navCollapsed]);

  useEffect(() => {
    if (read.kind === "ready") {
      sendSession({ type: "BOOT_RESULT", classification: classifyBootSnapshot(read.boot) });
    } else if (read.kind === "failed") {
      sendSession({ type: "BOOT_FAILED", message: read.message });
    }
  }, [read, sendSession]);

  const sessionMode = session.value as SessionMode;
  const shellActive = sessionMode === "fresh" || sessionMode === "readOnly";
  const currentPathname = routerState.location.pathname;

  // Operation-level typed session-loss diagnostics (`web_studio.auth.*`
  // account-session codes) route to the gate and re-run boot so the backend
  // confirms the state; narrower scope-loss diagnostics never arrive here.
  useEffect(
    () =>
      subscribeWebStudioAuthSessionLoss((diagnosticCode) => {
        sendSession({ type: "AUTH_LOST", diagnosticCode });
        requestRefresh();
      }),
    [sendSession, requestRefresh],
  );

  // Auth-shaped transitions: entering the gate clears protected per-session
  // browser state and retains the intended location in memory only; leaving
  // it restores the deep link solely through the backend-validated
  // route-state path, repairing deterministically to the default destination
  // when the backend rejects the restored route state.
  const wasUnauthenticated = useRef(false);
  useEffect(() => {
    if (sessionMode === "unauthenticated") {
      if (!wasUnauthenticated.current) {
        wasUnauthenticated.current = true;
        clearProtectedWebStudioBrowserState();
        captureIntendedStudioLocation({ pathname: currentPathname, search });
      }
      return;
    }
    if (sessionMode === "fresh" || sessionMode === "readOnly") {
      if (wasUnauthenticated.current) {
        wasUnauthenticated.current = false;
        const intended = takeIntendedStudioLocation();
        if (intended !== null && intended.pathname !== currentPathname) {
          void navigate({
            to: intended.pathname,
            search: validateScopeSearch(intended.search),
          } as Parameters<typeof navigate>[0]);
          return;
        }
      }
      consumeDeepLinkRepair("accepted");
      return;
    }
    if (sessionMode === "blocked") {
      if (wasUnauthenticated.current) {
        // The backend authorized the session but rejected the retained deep
        // link's route state in the same boot: repair to the default.
        wasUnauthenticated.current = false;
        resetIntendedStudioLocation();
        void navigate({ to: DEFAULT_STUDIO_DESTINATION, search: {} });
        return;
      }
      if (consumeDeepLinkRepair("rejected")) {
        void navigate({ to: DEFAULT_STUDIO_DESTINATION, search: {} });
      }
    }
  }, [sessionMode, currentPathname, search, navigate]);

  const workspaceCatalogQuery = useQuery({
    ...workspaceCatalogQueryOptions(client, scope, { generation: token }),
    enabled: shellActive,
  });
  const commandCatalogQuery = useQuery({
    ...commandCatalogQueryOptions(client, scope, { generation: token }),
    enabled: shellActive,
  });

  const workspaceCatalogData = workspaceCatalogQuery.data?.data;
  const workspaceOptions =
    workspaceCatalogData === undefined
      ? null
      : catalogOptionsFromRows(workspaceCatalogData.workspaceRows, "workspace");
  const worldviewOptions =
    workspaceCatalogData === undefined
      ? null
      : catalogOptionsFromRows(workspaceCatalogData.worldviewRows, "worldview");
  const environmentOptions =
    workspaceCatalogData === undefined
      ? null
      : catalogOptionsFromRows(workspaceCatalogData.environmentRows, "environment");

  // Optimistic-concurrency digest for scope switches: the freshest
  // backend-issued snapshot identity for the scope being switched away from.
  const mutationTargetDigest =
    (read.kind === "ready" ? read.boot.responseIdentity.snapshotIdentity : undefined) ??
    workspaceCatalogQuery.data?.responseIdentity.snapshotIdentity ??
    "sha256:web-studio-scope-unread";

  const switchMutation = useMutation({
    mutationFn: async (request: ScopeSwitchRequest): Promise<WebStudioSelectionOutcome> => {
      const envelope = buildMutationEnvelope({
        ...BROWSER_ACTOR,
        expectedState: { targetDigest: mutationTargetDigest },
      });
      if (request.field === "workspace") {
        return workspaceSelectionOutcome(
          await selectWorkspace(client, scope, envelope, { workspaceId: request.requested }),
        );
      }
      if (request.field === "worldview") {
        return worldviewSelectionOutcome(
          await selectWorldview(client, scope, envelope, { worldviewId: request.requested }),
        );
      }
      return environmentSelectionOutcome(
        await selectWorkspaceEnvironment(client, scope, envelope, {
          workspaceId: scope.workspaceId,
          environmentId: request.requested,
        }),
      );
    },
    onSuccess: (outcome, request) => {
      if (outcome.kind === "confirmed") {
        // The URL scope adopts the backend-confirmed value; every query key
        // carries the scope, so stale reads drop out on the next render.
        sendSession({ type: "SCOPE_CHANGED" });
        setScopeSwitch({ kind: "idle" });
        void navigate({
          to: ".",
          search: (previous) => ({
            ...previous,
            [SEARCH_KEY_BY_SWITCH_FIELD[request.field]]: outcome.value,
          }),
          replace: true,
        });
      } else {
        setScopeSwitch({
          kind: "failed",
          ...request,
          code: outcome.code,
          message: outcome.message,
        });
      }
    },
    onError: (error, request) => {
      setScopeSwitch({
        kind: "failed",
        ...request,
        code: "web_studio.selection.transport_failed",
        message: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const requestScopeSwitch = (field: ScopeSwitchField, requested: string): void => {
    if (scopeSwitch.kind === "pending") {
      return;
    }
    setScopeSwitch({ kind: "pending", field, requested });
    switchMutation.mutate({ field, requested });
  };

  const scopeSwitchValue: ScopeSwitchValue = {
    pending: scopeSwitch.kind === "pending",
    requestScopeSwitch,
  };

  const onRefresh = (): void => {
    sendSession({ type: "REFRESH_REQUESTED" });
    requestRefresh();
  };

  const currentPath = routerState.location.pathname;

  const goTo = (to: string): void => {
    void navigate({ to, search: (previous) => previous });
  };

  const nav = STUDIO_NAV_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.items.map((item) => ({
      id: item.id,
      label: item.label,
      icon: destinationIcon(item.icon),
      active: item.to === "/" ? currentPath === "/" : currentPath.startsWith(item.to),
      onSelect: () => goTo(item.to),
    })),
  }));

  const commandEntries: WebStudioCommandEntry[] | null =
    commandCatalogQuery.data === undefined
      ? null
      : commandEntriesFromCatalog(commandCatalogQuery.data.data);

  const paletteItems = [
    ...allDestinations().map((destination) => ({
      id: destination.id,
      label: `Go to ${destination.label}`,
      group: "Navigate",
      icon: destinationIcon(destination.icon),
    })),
    ...(commandEntries ?? []).map((entry) => ({
      id: `${PALETTE_COMMAND_PREFIX}${entry.id}`,
      label: entry.label,
      group: "Commands",
      shortcut: entry.shortcut,
      disabled: entry.disabled,
      disabledReason: entry.disabledReason,
      icon: destinationIcon("terminal"),
    })),
  ];

  const onPaletteSelect = (id: string): void => {
    setPaletteOpen(false);
    const destination = allDestinations().find((entry) => entry.id === id);
    if (destination !== undefined) {
      goTo(destination.to);
      return;
    }
    if (!id.startsWith(PALETTE_COMMAND_PREFIX)) {
      return;
    }
    const entry = commandEntries?.find(
      (candidate) => `${PALETTE_COMMAND_PREFIX}${candidate.id}` === id,
    );
    if (entry === undefined) {
      return;
    }
    const hinted =
      entry.destinationHint === undefined
        ? undefined
        : allDestinations().find((candidate) => candidate.id === entry.destinationHint);
    if (hinted !== undefined) {
      goTo(hinted.to);
      return;
    }
    // Honest no-op: the palette navigates; command execution stays on the
    // command's own surface with its confirmations and receipts.
    setCommandNotice(entry.label);
  };

  const runtimeStatus = read.kind === "ready" ? read.boot.compatibility.runtimeStatus : null;
  const bootSnapshot: WebStudioBootSnapshot | null = read.kind === "ready" ? read.boot : null;
  const fixtureSessionLabel =
    bootSnapshot === null ? null : readWebStudioFixtureSessionLabel(bootSnapshot);

  // Auth-shaped states render ONLY the sign-in gate: no shell chrome,
  // sidebar, command palette, inspector dock, selectors, or semantic-plane
  // scaffolding mounts until boot reports an authorized session.
  if (sessionMode === "unauthenticated") {
    return (
      <SignInGate
        scope={scope}
        boot={bootSnapshot}
        diagnosticCode={session.context.diagnosticCode}
        onBootRefresh={onRefresh}
      />
    );
  }

  // Non-auth boot failures keep their own typed diagnostic rendering — never
  // the sign-in gate — equally without shell or destination content.
  if (sessionMode === "blocked" || sessionMode === "failed") {
    return (
      <BootBlockedScreen
        mode={sessionMode}
        diagnosticCode={session.context.diagnosticCode}
        failureMessage={session.context.failureMessage}
        boot={bootSnapshot}
        theme={appearance.theme}
        onRetry={onRefresh}
      />
    );
  }

  if (sessionMode === "booting") {
    return (
      <main className="boot-pending-screen" aria-label="Loading Studio">
        <img
          src={appearance.theme === "dark" ? tarskiLockupDark : tarskiLockupLight}
          alt="tarski"
          className="app-brand-lockup"
        />
        <p className="muted" role="status">
          Loading Studio from the backend…
        </p>
      </main>
    );
  }

  return (
    <>
      <AppShell
        navMode={narrow || navCollapsed ? "rail" : "sidebar"}
        navLabel="Studio destinations"
        brand={
          <img
            src={appearance.theme === "dark" ? tarskiLockupDark : tarskiLockupLight}
            alt="tarski"
            className="app-brand-lockup"
          />
        }
        center={
          <div className="shell-center">
            <AppScopeControls
              scope={scope}
              workspaceOptions={workspaceOptions}
              worldviewOptions={worldviewOptions}
              environmentOptions={environmentOptions}
              switchPending={scopeSwitch.kind === "pending"}
              onSelectWorkspace={(value) => requestScopeSwitch("workspace", value)}
              onSelectWorldview={(value) => requestScopeSwitch("worldview", value)}
              onSelectEnvironment={(value) => requestScopeSwitch("environment", value)}
            />
            {runtimeStatus === null ? null : (
              <StatusPill
                tone={runtimeStatus === "production_authoritative" ? "green" : "amber"}
                label={runtimeStatus}
                dot
              />
            )}
            {fixtureSessionLabel === null ? null : (
              // Backend-labeled non-production fixture session (for example
              // local-studio-user): the label renders verbatim in
              // account-presenting chrome so the bypass is always visible.
              <StatusPill tone="amber" label={`fixture session: ${fixtureSessionLabel}`} dot />
            )}
          </div>
        }
        actions={
          <>
            {narrow ? null : (
              <IconButton
                label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
                aria-pressed={navCollapsed}
                onClick={() => setNavCollapsed((collapsed) => !collapsed)}
              >
                {navCollapsed ? (
                  <PanelLeftOpen size={16} aria-hidden="true" />
                ) : (
                  <PanelLeftClose size={16} aria-hidden="true" />
                )}
              </IconButton>
            )}
            <IconButton
              label={appearance.theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              onClick={() => appearance.toggleTheme()}
            >
              {appearance.theme === "dark" ? (
                <Sun size={16} aria-hidden="true" />
              ) : (
                <Moon size={16} aria-hidden="true" />
              )}
            </IconButton>
            <IconButton
              label={
                appearance.density === "compact"
                  ? "Switch to comfortable density"
                  : "Switch to compact density"
              }
              onClick={() => appearance.toggleDensity()}
            >
              <Rows3 size={16} aria-hidden="true" />
            </IconButton>
            <IconButton label="Refresh" onClick={onRefresh}>
              <RefreshCw size={16} aria-hidden="true" />
            </IconButton>
            <IconButton
              label={inspector.open ? "Hide inspector" : "Show inspector"}
              aria-pressed={inspector.open}
              onClick={() => inspector.setOpen(!inspector.open)}
            >
              <PanelRight size={16} aria-hidden="true" />
            </IconButton>
          </>
        }
        nav={nav}
        inspector={inspector.open ? <ShellInspectorDock /> : undefined}
      >
        <div className="app-content">
          <SessionModeBanner mode={sessionMode} diagnosticCode={session.context.diagnosticCode} />
          <ScopeSwitchContext.Provider value={scopeSwitchValue}>
            <Outlet />
          </ScopeSwitchContext.Provider>
          {scopeSwitch.kind === "idle" ? null : (
            <ScopeSwitchOverlay
              state={scopeSwitch}
              onRetry={(request) => {
                setScopeSwitch({ kind: "pending", ...request });
                switchMutation.mutate(request);
              }}
              onDismiss={() => setScopeSwitch({ kind: "idle" })}
            />
          )}
        </div>
      </AppShell>
      {commandNotice === null ? null : (
        <div className="shell-toast-region">
          <Toast title="Command needs its surface" onDismiss={() => setCommandNotice(null)}>
            {commandNotice} does not name a route destination; the palette only navigates. Run it
            from its own Studio surface with its confirmations and receipts.
          </Toast>
        </div>
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        label="Studio command palette"
        placeholder="Search destinations and commands"
        items={paletteItems}
        onSelect={onPaletteSelect}
      />
    </>
  );
}

/**
 * Non-auth fail-closed boot rendering: contract mismatch, scope rejection,
 * and network failure keep their typed diagnostics and recovery actions —
 * never the sign-in gate — and never mount shell chrome or destination
 * content. Every field shown comes from the backend envelope (or, for
 * transport failures, the non-authoritative browser error message).
 */
function BootBlockedScreen({
  mode,
  diagnosticCode,
  failureMessage,
  boot,
  theme,
  onRetry,
}: {
  mode: "blocked" | "failed";
  diagnosticCode: string | null;
  failureMessage: string | null;
  boot: WebStudioBootSnapshot | null;
  theme: string;
  onRetry: () => void;
}): JSX.Element {
  const diagnostic: WebStudioDiagnostic | null =
    mode === "blocked" ? (boot?.diagnostics[0]?.diagnostic ?? null) : null;
  const authorizationState =
    mode === "blocked"
      ? ((boot?.data.routeState ?? boot?.data.authorizedRouteState)?.authorizationState ?? null)
      : null;
  return (
    <main className="boot-blocked-screen" aria-label="Studio unavailable">
      <div className="boot-blocked-screen__panel">
        <img
          src={theme === "dark" ? tarskiLockupDark : tarskiLockupLight}
          alt="tarski"
          className="app-brand-lockup"
        />
        <SessionModeBanner mode={mode} diagnosticCode={diagnosticCode} />
        {mode === "failed" ? (
          <div role="alert" className="boot-blocked-screen__diagnostic">
            <p>
              The backend boot read failed before any authoritative state was available:{" "}
              {failureMessage ?? "unknown transport failure"}
            </p>
          </div>
        ) : (
          <div role="status" className="boot-blocked-screen__diagnostic">
            {diagnostic === null ? (
              <p>
                The backend refused this session scope
                {diagnosticCode === null ? "." : ":"}{" "}
                {diagnosticCode === null ? null : <span className="mono">{diagnosticCode}</span>}
              </p>
            ) : (
              <>
                <p>
                  <span className="mono">{diagnostic.code}</span> {diagnostic.message}
                </p>
                {authorizationState === null ? null : (
                  <p className="muted">
                    Authorization state: <span className="mono">{authorizationState}</span>
                  </p>
                )}
              </>
            )}
          </div>
        )}
        <Button variant="secondary" onClick={onRetry}>
          Retry boot
        </Button>
      </div>
    </main>
  );
}

/**
 * Blocking scope-switch overlay: a full-content scrim with a status line
 * while the select operation is in flight, becoming a fail-closed alert
 * (backend diagnostic, retry, dismiss) when the backend refuses. The
 * scope never changes until the backend confirms it.
 */
function ScopeSwitchOverlay({
  state,
  onRetry,
  onDismiss,
}: {
  state: Exclude<ScopeSwitchState, { kind: "idle" }>;
  onRetry: (request: ScopeSwitchRequest) => void;
  onDismiss: () => void;
}): JSX.Element {
  if (state.kind === "pending") {
    return (
      <div className="scope-switch-overlay">
        <div className="scope-switch-overlay__panel" role="status">
          <span className="scope-switch-overlay__title">Switching {state.field}</span>
          <span className="scope-switch-overlay__value">{state.requested}</span>
          <span className="scope-switch-overlay__note">
            Waiting for the backend to confirm the new scope. Stale content stays covered until
            hydration completes.
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="scope-switch-overlay">
      <div className="scope-switch-overlay__panel" role="alert">
        <span className="scope-switch-overlay__title">Switch refused — scope unchanged</span>
        <span className="scope-switch-overlay__value">{state.code}</span>
        <span className="scope-switch-overlay__note">{state.message}</span>
        <div className="scope-switch-overlay__actions">
          <Button
            variant="secondary"
            onClick={() => onRetry({ field: state.field, requested: state.requested })}
          >
            Retry switch
          </Button>
          <Button variant="ghost" onClick={onDismiss}>
            Keep current scope
          </Button>
        </div>
      </div>
    </div>
  );
}
